/**
 * Quick task 260602-g6r — Hover-trendline popover on username cell.
 *
 * Hover-triggered absolute-positioned popover anchored to the username `<td>`
 * cell on the Billability Report. Renders a Recharts dual-axis line chart of
 * the trailing-12-month margin1 (left axis, CZK) and commitCount (right axis)
 * for a single user.
 *
 * The fetch is gated by `isOpen` via TanStack Query's `enabled` flag — the
 * hook does NOT call the backend until the popover opens. A 5-minute
 * `staleTime` on the hook avoids refetches as the cursor moves between rows
 * inside the same session.
 *
 * Renders into `document.body` via `createPortal` so the popover does not get
 * clipped by the report table's `overflow-x-auto` wrapper.
 */
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useBillabilityUserTrend } from '../../hooks/useReports';
import { Spinner } from '../common/Spinner';
import { formatMonthShort } from '../../utils/dateFormat';

export interface UsernameTrendPopoverProps {
  userId: number;
  userName: string;
  isOpen: boolean;
  anchorRect?: ReturnType<HTMLElement['getBoundingClientRect']> | null;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

interface ChartPoint {
  yearMonth: string;
  label: string;
  margin1: number;
  commitCount: number;
}

/**
 * `'2026-06'` → `'čvn'` short month label (cs-CZ). Always-2-digit MM means the
 * parse is safe across locales.
 */
function monthLabel(yearMonth: string): string {
  const [yearStr, monthStr] = yearMonth.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr) - 1;
  if (Number.isNaN(year) || Number.isNaN(month)) return yearMonth;
  return formatMonthShort(`${year}-${String(month + 1).padStart(2, '0')}`); // MKS-351
}

export function UsernameTrendPopover({
  userId,
  userName,
  isOpen,
  anchorRect,
  onMouseEnter,
  onMouseLeave,
}: UsernameTrendPopoverProps) {
  const { data, isLoading, error } = useBillabilityUserTrend(userId, isOpen);

  const chartPoints = useMemo<ChartPoint[]>(() => {
    if (!data?.months) return [];
    return data.months.map((m) => ({
      yearMonth: m.yearMonth,
      label: monthLabel(m.yearMonth),
      margin1: m.margin1,
      commitCount: m.commitCount,
    }));
  }, [data]);

  if (!isOpen) return null;

  // Anchor under the cell; if no rect, fall back to top-left (defensive — the
  // parent always passes a rect when isOpen is true).
  const top = anchorRect ? anchorRect.bottom + window.scrollY + 4 : 0;
  const left = anchorRect ? anchorRect.left + window.scrollX : 0;

  const allZero =
    data?.months.every((m) => m.margin1 === 0 && m.commitCount === 0) ?? false;

  const popover = (
    <div
      role="dialog"
      aria-label={`Trend for ${userName}`}
      data-testid="username-trend-popover"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="absolute z-50 bg-white shadow-lg rounded-lg border border-gray-200 p-3 w-80"
      style={{ top, left }}
    >
      <div className="text-xs text-gray-600 mb-1">{userName} · last 12 months</div>
      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <Spinner size="sm" />
        </div>
      )}
      {error && (
        <div className="py-2">
          <p className="text-xs text-red-600">Failed to load trend</p>
          <p className="text-xs text-gray-500">{(error as Error).message}</p>
        </div>
      )}
      {data && !isLoading && !error && (
        <>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartPoints} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="margin1"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                data-testid="line-margin1"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="commitCount"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                data-testid="line-commits"
              />
            </LineChart>
          </ResponsiveContainer>
          {allZero && (
            <p className="text-xs text-gray-500 mt-1">
              No data for this user in the last 12 months.
            </p>
          )}
        </>
      )}
    </div>
  );

  return createPortal(popover, document.body);
}

export default UsernameTrendPopover;
